import React, { useState } from 'react';
import { XIcon, PlusIcon, TrashIcon, EditIcon } from './icons/AdminIcons';
import './PipelineStagesModal.css';

interface PipelineStage {
  id: string;
  label: string;
  color: string;
  position: number;
  icon?: string;
}

interface PipelineStagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  stages: PipelineStage[];
  onSave: (stages: PipelineStage[]) => Promise<void>;
  pipelineId?: number | null;
  pipelineName?: string;
  onUpdatePipelineName?: (name: string) => Promise<void>;
  /** Abre el modal de gestionar propiedades (campos del pipeline). Si se proporciona, se muestra el botón en el modal. */
  onOpenProperties?: () => void;
}

const PipelineStagesModal: React.FC<PipelineStagesModalProps> = ({
  isOpen, onClose, stages, onSave,
  pipelineId, pipelineName = '', onUpdatePipelineName, onOpenProperties
}) => {
  const [localStages, setLocalStages] = useState<PipelineStage[]>(stages);
  const [editingStage, setEditingStage] = useState<string | null>(null);
  const [editingStageData, setEditingStageData] = useState<Partial<PipelineStage>>({});
  const [newStage, setNewStage] = useState<Partial<PipelineStage>>({ label: '', color: '#94a3b8', position: stages.length });
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmStage, setDeleteConfirmStage] = useState<string | null>(null);
  const [draggedStageId, setDraggedStageId] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [localPipelineName, setLocalPipelineName] = useState(pipelineName);
  const [isSavingName, setIsSavingName] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setLocalStages([...stages]);
      setLocalPipelineName(pipelineName);
    }
  }, [isOpen, stages, pipelineName]);

  const handleAddStage = () => {
    if (!newStage.label || !newStage.label.trim()) {
      alert('El nombre de la etapa es requerido');
      return;
    }
    
    const stageId = newStage.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    const newStageComplete: PipelineStage = {
      id: stageId,
      label: newStage.label.trim(),
      color: newStage.color || '#94a3b8',
      position: localStages.length,
      icon: newStage.icon
    };
    
    setLocalStages([...localStages, newStageComplete]);
    setNewStage({ label: '', color: '#94a3b8', position: localStages.length + 1 });
  };

  const handleStartEdit = (stageId: string) => {
    const stage = localStages.find(s => s.id === stageId);
    if (stage) {
      setEditingStage(stageId);
      setEditingStageData({ label: stage.label, color: stage.color });
    }
  };

  const handleSaveEdit = (stageId: string) => {
    setLocalStages(localStages.map(stage => 
      stage.id === stageId ? { ...stage, ...editingStageData } : stage
    ));
    setEditingStage(null);
    setEditingStageData({});
  };

  const handleCancelEdit = () => {
    setEditingStage(null);
    setEditingStageData({});
  };

  const handleDeleteStage = (stageId: string) => {
    if (localStages.length <= 1) {
      alert('Debe haber al menos una etapa');
      return;
    }
    setDeleteConfirmStage(stageId);
  };

  const confirmDelete = () => {
    if (deleteConfirmStage) {
      const updatedStages = localStages
        .filter(stage => stage.id !== deleteConfirmStage)
        .map((stage, index) => ({ ...stage, position: index }));
      setLocalStages(updatedStages);
      setDeleteConfirmStage(null);
    }
  };

  const handleDragStart = (e: React.DragEvent, stageId: string) => {
    setDraggedStageId(stageId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (!draggedStageId) return;

    const dragIndex = localStages.findIndex(s => s.id === draggedStageId);
    if (dragIndex === -1 || dragIndex === dropIndex) {
      setDraggedStageId(null);
      setDragOverIndex(null);
      return;
    }

    const newStages = [...localStages];
    const [removed] = newStages.splice(dragIndex, 1);
    newStages.splice(dropIndex, 0, removed);
    
    // Actualizar posiciones
    const updatedStages = newStages.map((stage, index) => ({
      ...stage,
      position: index
    }));

    setLocalStages(updatedStages);
    setDraggedStageId(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedStageId(null);
    setDragOverIndex(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(localStages);
      onClose();
    } catch (error) {
      alert('Error al guardar las etapas');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePipelineName = async () => {
    if (!onUpdatePipelineName || pipelineId == null || !localPipelineName.trim()) return;
    setIsSavingName(true);
    try {
      await onUpdatePipelineName(localPipelineName.trim());
    } catch (e) {
      alert('Error al actualizar el nombre del pipeline');
    } finally {
      setIsSavingName(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pipeline-stages-modal-overlay" onClick={onClose}>
      <div className="pipeline-stages-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pipeline-stages-modal-header">
          <h2>Gestionar pipeline</h2>
          <button className="pipeline-stages-modal-close" onClick={onClose}>
            <XIcon width={20} height={20} />
          </button>
        </div>

        <div className="pipeline-stages-modal-body">
          {pipelineId != null && onUpdatePipelineName && (
            <div className="pipeline-stages-name-section">
              <label className="pipeline-stages-name-label">Nombre del pipeline</label>
              <div className="pipeline-stages-name-row">
                <input
                  type="text"
                  className="pipeline-stage-input"
                  value={localPipelineName}
                  onChange={(e) => setLocalPipelineName(e.target.value)}
                  placeholder="Ej: Scesde26, Ventas"
                  onKeyDown={(e) => e.key === 'Enter' && handleSavePipelineName()}
                />
                <button
                  type="button"
                  className="pipeline-stage-action-btn"
                  onClick={handleSavePipelineName}
                  disabled={isSavingName || !localPipelineName.trim()}
                >
                  {isSavingName ? 'Guardando...' : 'Guardar nombre'}
                </button>
              </div>
            </div>
          )}

          <div className="pipeline-stages-list">
            {localStages.map((stage, index) => (
              <div 
                key={stage.id} 
                className={`pipeline-stage-item ${draggedStageId === stage.id ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
                draggable={editingStage !== stage.id}
                onDragStart={(e) => handleDragStart(e, stage.id)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                <div className="pipeline-stage-item-content">
                  {editingStage === stage.id ? (
                    <>
                      <input
                        type="text"
                        value={editingStageData.label || ''}
                        onChange={(e) => setEditingStageData({ ...editingStageData, label: e.target.value })}
                        className="pipeline-stage-input"
                        placeholder="Nombre de la etapa"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveEdit(stage.id);
                          } else if (e.key === 'Escape') {
                            handleCancelEdit();
                          }
                        }}
                      />
                      <input
                        type="color"
                        value={editingStageData.color || '#94a3b8'}
                        onChange={(e) => setEditingStageData({ ...editingStageData, color: e.target.value })}
                        className="pipeline-stage-color-input"
                      />
                      <div className="pipeline-stage-actions">
                        <button
                          onClick={() => handleSaveEdit(stage.id)}
                          className="pipeline-stage-action-btn"
                        >
                          Guardar
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="pipeline-stage-action-btn"
                        >
                          Cancelar
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="pipeline-stage-info">
                        <div
                          className="pipeline-stage-color-preview"
                          style={{ backgroundColor: stage.color }}
                        />
                        <span className="pipeline-stage-label">{stage.label}</span>
                        <span className="pipeline-stage-position">Posición: {index + 1}</span>
                        {editingStage !== stage.id && (
                          <span className="pipeline-stage-drag-handle" title="Arrastrar para reordenar">⋮⋮</span>
                        )}
                      </div>
                      <div className="pipeline-stage-actions">
                        <button
                          onClick={() => handleStartEdit(stage.id)}
                          className="pipeline-stage-action-btn"
                          title="Editar"
                        >
                          <EditIcon width={16} height={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteStage(stage.id)}
                          className="pipeline-stage-action-btn delete"
                          title="Eliminar"
                          disabled={localStages.length <= 1}
                        >
                          <TrashIcon width={16} height={16} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="pipeline-stages-add">
            <h3>Agregar Nueva Etapa</h3>
            <div className="pipeline-stages-add-form">
              <input
                type="text"
                value={newStage.label || ''}
                onChange={(e) => setNewStage({ ...newStage, label: e.target.value })}
                className="pipeline-stage-input"
                placeholder="Nombre de la etapa"
              />
              <input
                type="color"
                value={newStage.color || '#94a3b8'}
                onChange={(e) => setNewStage({ ...newStage, color: e.target.value })}
                className="pipeline-stage-color-input"
                title="Color"
              />
              <button
                onClick={handleAddStage}
                className="pipeline-stage-add-btn"
              >
                <PlusIcon width={16} height={16} /> Agregar
              </button>
            </div>
          </div>
        </div>

        <div className="pipeline-stages-modal-footer">
          <div className="pipeline-stages-modal-footer-left">
            {onOpenProperties != null && pipelineId != null && (
              <button
                type="button"
                className="pipeline-stages-modal-properties"
                onClick={() => {
                  onClose();
                  onOpenProperties();
                }}
              >
                Gestionar propiedades
              </button>
            )}
          </div>
          <div className="pipeline-stages-modal-footer-right">
            <button
              onClick={onClose}
              className="pipeline-stages-modal-cancel"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="pipeline-stages-modal-save"
              disabled={isSaving}
            >
              {isSaving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>
      </div>

      {/* Modal de confirmación para eliminar */}
      {deleteConfirmStage && (
        <div className="pipeline-stages-modal-overlay" onClick={() => setDeleteConfirmStage(null)}>
          <div className="pipeline-stages-modal delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pipeline-stages-modal-header">
              <h2>Confirmar Eliminación</h2>
              <button className="pipeline-stages-modal-close" onClick={() => setDeleteConfirmStage(null)}>
                <XIcon width={20} height={20} />
              </button>
            </div>
            <div className="pipeline-stages-modal-body">
              <p>¿Está seguro de que desea eliminar la etapa "{localStages.find(s => s.id === deleteConfirmStage)?.label}"?</p>
              <p style={{ fontSize: '14px', color: '#64748b', marginTop: '8px' }}>Esta acción no se puede deshacer.</p>
            </div>
            <div className="pipeline-stages-modal-footer">
              <button
                onClick={() => setDeleteConfirmStage(null)}
                className="pipeline-stages-modal-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="pipeline-stages-modal-save"
                style={{ background: '#ef4444' }}
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineStagesModal;
